import React, { useCallback, useContext, useEffect, useState } from 'react';
import { bool, object } from 'prop-types';
import { Button, Form, Stack } from 'react-bootstrap';

import { ToastContext } from './context/ToastContext';
import AppContext from './context/AppContext';
import getRecaptchaToken from '../helpers/getRecaptchaToken';

import '../styles/PageForm.css';
import PageFormField from './PageFormField';

const DEFAULT_FIELD_DIRECTION = 'row';
const FILES_SUFFIX = '-files';

const groupFieldsByRowAndOrder = (fields = []) => {
    return fields.reduce((acc, field) => {
        const key = `${field.direction}-${field.order}`;
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(field);
        return acc;
    }, {});
};

function PageForm({ form, isMobile }) {
    const [data, setData] = useState({});
    const { sendMessage } = useContext(ToastContext);
    const { appContext } = useContext(AppContext);
    const [loading, setLoading] = useState(false);
    const groupedFields = groupFieldsByRowAndOrder(form.Fields);
    const [formSubmitted, setFormSubmitted] = useState(false);

    const resetData = useCallback(() => {
        const data = {};
        form.Fields.forEach((field) => {
            data[field.id] = '';
        });
        setData(data);
    }, [form, setData]);

    const onFormSubmit = useCallback(async (e) => {
        e.preventDefault();

        const formData = new FormData();
        for (const key in data) {
            if (key.includes(FILES_SUFFIX)) {
                for (const file of data[key]) {
                    formData.append(key, file);
                }
            } else if (!Object.keys(data).includes(`${key}${FILES_SUFFIX}`)) {
                formData.append(key, data[key]);
            }
        }

        try {
            setLoading(true);
            const url = new URL(`/api/forms/${form.id}/sendEmail`, process.env.APP_URL);
            const response = await fetch(url, {
                method: 'POST',
                body: formData,
                headers: {
                    'x-grecaptcha-token': await getRecaptchaToken(appContext.config.recaptchaSiteKey),
                },
            });
            const { success, error } = await response.json();

            if (error) {
                sendMessage(error, 'danger');
            }

            if (success) {
                sendMessage('Email sent to the store', 'success');
                setFormSubmitted(true);
                resetData();
            }
        } catch (e) {
            sendMessage(e.message, 'danger');
        } finally {
            setLoading(false);
        }
    }, [data, form]);

    useEffect(() => {
        resetData();
    }, [resetData]);

    return (
        <div
            className={'page-form'}
        >
            <h5>{form.title}</h5>
            <Form
                onSubmit={onFormSubmit}
            >
                {Object.keys(groupedFields).map((key) => {
                    if (key.startsWith(DEFAULT_FIELD_DIRECTION)) {
                        return groupedFields[key]?.map((field) => {
                            return <PageFormField
                                key={field.id}
                                field={field}
                                data={data}
                                setData={setData}
                                formSubmitted={formSubmitted}
                                setFormSubmitted={setFormSubmitted}
                            />;
                        });
                    } else {
                        return (
                            <Stack
                                key={key}
                                gap={3}
                                direction={isMobile ? 'vertical' : 'horizontal'}
                                className={'align-items-start'}
                            >
                                {groupedFields[key]?.map((field) => {
                                    return <PageFormField
                                        key={field.id}
                                        field={field}
                                        data={data}
                                        setData={setData}
                                        formSubmitted={formSubmitted}
                                        setFormSubmitted={setFormSubmitted}
                                    />;
                                })}
                            </Stack>
                        );
                    }
                })}
                <h5>{form.description}</h5>
                <div
                    className={'d-grid gap-2'}
                >
                    <Button
                        variant={'primary'}
                        type={'submit'}
                        disabled={loading}
                        size={'lg'}
                    >
                    Submit
                    </Button>
                </div>
            </Form>
        </div>
    );
}

PageForm.propTypes = {
    form: object,
    isMobile: bool,
};

export default PageForm;